import { Provider, inject } from '@angular/core';
import { COCKPIT_DEMO_MODE, COCKPIT_STORE } from './cockpit-store.interface';
import { CockpitFirestoreStore } from './cockpit-firestore-store.service';
import { CockpitCardActionsService } from '@app/pages/apps/cockpit/services/cockpit-card-actions.service';
import { CockpitDemoPreload } from './cockpit-mock-store.preload';

// Re-exported for existing consumers (e.g. `cockpit.component.ts`) — the
// implementation moved to its own file so `cockpit-mock-store.preload.ts`
// can import it without a module cycle back to this file.
export { isCockpitDemoMode } from './cockpit-demo-mode';

/**
 * Route-level provider that wires `COCKPIT_STORE` to the live Firestore-backed
 * store — or, in dev with `?demo=1`, to the in-memory `CockpitMockStore`.
 * Wired on the cockpit route, declared inline in `app.routes.ts`.
 *
 * The demo-mode decision and (if needed) the `import()` of `CockpitMockStore`
 * both happen once, ahead of time, in `cockpitMockStorePreloadResolver` —
 * required on every route that uses this provider (see that resolver's
 * doc). `COCKPIT_DEMO_MODE` and `COCKPIT_STORE` below just read the result;
 * neither may import `CockpitMockStore` statically, or #513 point 55 is back.
 */
export function provideCockpitStore(): Provider[] {
  return [
    CockpitFirestoreStore,
    {
      provide: COCKPIT_DEMO_MODE,
      useFactory: (): boolean => inject(CockpitDemoPreload).demoMode,
    },
    {
      provide: COCKPIT_STORE,
      useFactory: (): unknown => {
        if (!inject(COCKPIT_DEMO_MODE)) return inject(CockpitFirestoreStore);
        const ctor = inject(CockpitDemoPreload).mockStoreCtor;
        if (!ctor) {
          throw new Error(
            'CockpitMockStore requested but never preloaded — this route is ' +
              'missing cockpitMockStorePreloadResolver in its `resolve` config.'
          );
        }
        return inject(ctor);
      },
    },
    // Route-scoped: depends on COCKPIT_STORE above, so it must live in this
    // same injector — never `providedIn: 'root'` (would crash with NG0201).
    CockpitCardActionsService,
  ];
}
